import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, GridSlots, PropsFromSlot, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";

/**
 * CustomToolbar
 */

const CustomToolbar = ({ toolbarRightSlot }: any) => {
  return (
    <Toolbar>
      <Tooltip title="Colunas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
};

export const CustomFooter = ({ information }: PropsFromSlot<GridSlots['footer']>) => {
  return (
    <>
      {information &&
        <div className="w-full flex flex-col items-end py-2 px-2 border-t border-border-light-mode dark:border-border-dark-mode">
          <div className="flex flex-col text-[1.15rem]">
            <div className="w-[20rem] flex flex-row justify-between">
              <span>SUB-TOTAL: </span>
              <span className='font-bold'>R$ {information.subtotal.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="w-[20rem] flex flex-row justify-between">
              <span>IMPOSTO: </span>
              <span className='font-bold'>R$ {information.imposto.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="w-[20rem] flex flex-row justify-between">
              <span>FRETE: </span>
              <span className='font-bold'>R$ {information.frete.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="w-[20rem] flex flex-row justify-between">
              <span>DESCONTO: </span>
              <span className='font-bold'>R$ {information.desconto.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="w-[20rem] flex flex-row justify-between">
              <span>ACRÉSCIMO: </span>
              <span className='font-bold'>R$ {information.acrescimo.toFixed(2).replace('.', ',')}</span>
            </div>
            <div className="w-[20rem] flex flex-row justify-between">
              <span>TOTAL: </span>
              <span className="font-bold text-red-500">R$ {information.total.toFixed(2).replace('.', ',')}</span>
            </div>
          </div>
        </div>
      }
    </>
  );
}

/**
 * CustomStyledDataGrid
 */

interface Props extends DataGridProProps {
  tableName: string;
  minHeight?: string;
  height?: string;
  maxHeight?: string;
  information?: {
    subtotal: number;
    imposto: number;
    frete: number;
    desconto: number;
    acrescimo: number;
    total: number;
  };
}

export const CustomStyledDataGrid = ({ tableName, minHeight, information, height = "auto", ...props }: Props) => {
  const [columnsVisibilityModel, handleColumnsVisibilityModel] = useColumnSelectionConfig({
    tabela: tableName,
  });

  const mode = useContext(AppContext).theme;
  const StyledDataGrid = useMemo(() => {
    return styled(DataGridPro)(({ theme }) => ({
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: "#aeeece",
      },
      "& .MuiDataGrid-columnSeparator": {
        color: "black",
      },
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
      "& .MuiDataGrid-toolbar .MuiIconButton-root": {
        color: mode === "light" ? "black" : "white",
      },
      "& .MuiDataGrid-columnHeaderTitleContainerContent": {
        "& .MuiCheckbox-root": {
          color: "black",
        },
      },
      "& .MuiIconButton-root": {
        color: "black",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        wordBreak: "normal",
        whiteSpace: "normal",
        lineHeight: 1.5,
        color: "black",
      },
      backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
    }));
  }, [mode]);

  return (
    <Box sx={{ display: "flex", height: height, width: "100%", minHeight: minHeight }}>
      <StyledDataGrid
        showToolbar={true}
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        slotProps={{
          columnsPanel: {
            style: {
              color: mode === "dark" ? "#ffffff" : "#000000",
            },
          },
          footer: {
            information,
          } as PropsFromSlot<GridSlots['footer']>,
        }}
        columnVisibilityModel={columnsVisibilityModel}
        disableColumnSorting={true}
        multipleColumnsSortingMode="always"
        onColumnVisibilityModelChange={handleColumnsVisibilityModel}
        disableRowSelectionExcludeModel={true}

        {...props}

        disableColumnMenu={false}
        localeText={{
          noRowsLabel: "Não existem itens para mostrar!",

          columnsManagementSearchTitle: "Buscar",
          columnsManagementNoColumns: "Não há colunas",
          columnsManagementShowHideAllText: "Exibir/Esconder",
          columnsManagementReset: "Reiniciar",
          columnsManagementDeleteIconLabel: "Limpar",

          pinToLeft: "Fixar a esquerda",
          pinToRight: "Fixar a direita",
          unpin: "Desfixar",

          columnMenuShowColumns: "Exibir colunas",
          columnMenuManageColumns: "Gerenciar colunas",

          columnMenuHideColumn: "Esconder coluna",
          columnMenuUnsort: "Desordenar",
          columnMenuSortAsc: "Ordenar ASC",
          columnMenuSortDesc: "Ordenar DESC",
        }}
      />
    </Box>
  );
};
